import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { OpenFeatureProvider } from '@openfeature/react-sdk';

import { useAuth } from '@frontegg/react';

import { useAuthContext } from '~/shared/context/useAuthContext';

import { TenantFlagResolutionReadyContext } from './featureFlagResolution';
import { fetchLaunchDarklyInitConfig } from './fetchLaunchDarklyInitConfig';
import { initializeFallbackProvider, initializeFeatureFlagProvider } from './openFeatureSetup';

interface FeatureFlagProviderProps {
  children: ReactNode;
}

// Install the fallback provider once, synchronously, so the app never waits on the
// network to render: gated components suspend individually via FeatureGate/useTenantFlag.
let fallbackProviderReady: Promise<void> | undefined;
function ensureFallbackProviderInstalled(): Promise<void> {
  if (!fallbackProviderReady) {
    fallbackProviderReady = initializeFallbackProvider();
  }
  return fallbackProviderReady;
}
void ensureFallbackProviderInstalled();

export function FeatureFlagProvider({ children }: FeatureFlagProviderProps) {
  const { user } = useAuth();
  const { isAuthenticated, activeTenantId } = useAuthContext();
  const shouldManageFlags = Boolean(isAuthenticated);
  const contextReady = Boolean(shouldManageFlags && activeTenantId);
  const initSequence = useRef(0);
  const [tenantFlagResolutionReady, setTenantFlagResolutionReady] = useState(!shouldManageFlags);

  const { data: initConfig, isError, isFetched } = useQuery({
    queryKey: ['launchDarklyContext', activeTenantId ?? ''],
    queryFn: () => fetchLaunchDarklyInitConfig({ accessToken: user?.accessToken }),
    enabled: contextReady && Boolean(user?.accessToken),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    if (!shouldManageFlags) {
      setTenantFlagResolutionReady(true);
      return;
    }

    if (!contextReady) {
      setTenantFlagResolutionReady(false);
      return;
    }

    const sequence = ++initSequence.current;
  let cancelled = false;
    setTenantFlagResolutionReady(false);
    const shouldApply = () => sequence === initSequence.current;
    const contextFetchFailed = isFetched && isError;

    // During account transitions, fail closed immediately by applying defaults until
    // the next account context is resolved.
    if (!initConfig && !contextFetchFailed) {
      void (async () => {
        await initializeFallbackProvider({ shouldApply });

        if (cancelled || !shouldApply()) {
          return;
        }

        setTenantFlagResolutionReady(true);
      })();

      return () => {
        cancelled = true;
        if (sequence === initSequence.current) {
          initSequence.current += 1;
        }
      };
    }

    // The provider swap below never unmounts consumers: @openfeature/react-sdk reacts
    // to the provider-changed event on the existing OpenFeatureProvider tree in place.
    void (async () => {
      await initializeFeatureFlagProvider({
        initConfig,
        contextFetchFailed,
        shouldApply,
      });

      if (cancelled || sequence !== initSequence.current) {
        return;
      }

      setTenantFlagResolutionReady(true);
    })();

    return () => {
      cancelled = true;
      if (sequence === initSequence.current) {
        initSequence.current += 1;
      }
    };
  }, [shouldManageFlags, contextReady, initConfig, isFetched, isError, activeTenantId]);

  if (!shouldManageFlags) {
    return <>{children}</>;
  }

  return (
    <OpenFeatureProvider suspendUntilReady={false}>
      <TenantFlagResolutionReadyContext.Provider value={tenantFlagResolutionReady}>
        {children}
      </TenantFlagResolutionReadyContext.Provider>
    </OpenFeatureProvider>
  );
}
